/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { v4 as uuidV4 } from 'uuid';

import { BaseInputConfig, InputType, AddressInputConfig } from '../../form/TaskInputs/types';
import { NotaryAPI } from '../../../api/notary/types';
import { DocumentsAPI } from '../../../api/common/documents';
import { IAttachment } from '../../../components/common/AttachmentCard/types';
import { addressUtils } from '@components/form/Inputs/AddressInput/utils';
import { formatFullName, formatFullTimeStampAtBrowserTimeZone, formatMDY, formatYMD } from '@utils/formatting';
import { compact, extend, find, flatten, get, reduce } from '@utils/lodash';
import { DocumentStatus } from '@shared/constants/document';
import { CENTS, DEFAULT_FEE_VALUE } from '@shared/constants/journalEntry';

const credentialTypeChoices = [
  { value: 'NOT_PROVIDED', text: 'Not Provided' },
  { value: 'PASSPORT', text: 'Passport' },
  { value: 'DRIVERS_LICENSE', text: "Driver's License" },
  { value: 'ID_CARD', text: 'ID Card' },
  { value: 'PASSPORT_CARD', text: 'Passport Card' },
  { value: 'PERMANENT_RESIDENT_CARD', text: 'Permanent Resident Card' },
  { value: 'TRIBAL_ID_CARD', text: 'Tribal ID Card' },
  { value: 'EMPLOYMENT_CARD', text: 'Employment Card' },
  { value: 'WEAPONS_LICENSE', text: 'Weapons License' },
];

const getSignerNames = (signers: NotaryAPI.JournalEntryField[][]) => {
  return signers.map(signer => {
    const firstName = find(signer, { property: 'firstName' })?.value;
    const middleName = find(signer, { property: 'middleName' })?.value;
    const lastName = find(signer, { property: 'lastName' })?.value;
    const suffix = find(signer, { property: 'suffix' })?.value;
    return formatFullName({ firstName, middleName, lastName, suffix });
  });
};

const getAppointmentCardInfo = (apptInfo: NotaryAPI.JournalEntryField[], signerNames: string) => {
  const normalizedInfo = apptInfo.map(info => {
    // if info value is not a date string, the original value will be returned
    const formattedValue = info.type === 'timestamp' ? formatFullTimeStampAtBrowserTimeZone(info.value) : info.value;
    return { primary: info.label, value: formattedValue };
  });
  // Insert sign names to show as the second item in the appointment info card
  normalizedInfo.splice(1, 0, { primary: 'Signer name', value: signerNames });
  return normalizedInfo;
};

const getSignerInputs = (signer: NotaryAPI.JournalEntryField[], i: number): BaseInputConfig[] => {
  // TODO: make this group input
  return compact(
    signer.map(field => {
      // generate input configs for signer fields with an input type
      if (!!field.type) {
        return {
          // redux-form uses property access notation (e.g. foo[1].bar) to namespace input values.
          // so we can't use bracket for namespacing signer inputs. Instead, we use bullet operator
          // to be able to use redux form correctly
          id: `${field.property}∙${i}`,
          label: field.label,
          type: field.type as InputType,
          required: false,
          choices: field.type === 'dropdown' && credentialTypeChoices,
          googleApiKey: field.googleApiKey,
        };
      }
      return null;
    }),
  );
};

const getSignerCardInfo = (signer: NotaryAPI.JournalEntryField[], signerName: string) => {
  const normalizedInfo = signer.map(info => {
    if (info.property === 'signatureFileBase64') {
      return { primary: info.property, value: info.value };
    }
    if (info.property === 'isInCalifornia') {
      if (info.value === undefined || info.value === null) {
        return null;
      }
      return { primary: 'Physically present in California?', value: info.value ? 'Yes' : 'No' };
    }
    if (!!info.type) {
      const infoValue = info.type === 'address' ? addressUtils.dataModelAddressToString(info.value) : info.value || '-';
      return { primary: info.label, value: infoValue };
    }
    return null;
  });
  return [{ primary: 'Signer name', value: signerName }, ...compact(normalizedInfo)];
};

const getNoteInput = (): BaseInputConfig => {
  return { id: `notes`, type: 'longText', required: false, label: 'Journal Entry Notes' };
};

const getNotaryLocationInput = (
  entryNotaryLocation: NotaryAPI.JournalEntryResponse['notaryLocation'],
): AddressInputConfig | undefined => {
  if (!entryNotaryLocation) {
    return;
  }
  return {
    id: `notaryLocation`,
    type: 'address',
    required: true,
    googleApiKey: entryNotaryLocation.googleApiKey || '',
  };
};

const getInitialValues = (
  signers: NotaryAPI.JournalEntryField[][],
  notes?: string,
  notaryLocation?: NotaryAPI.JournalEntryField,
) => {
  const signersInitialValues = signers.map((signer, i) => {
    // generate input initial value for signer fields with an input type
    const signerInputValues = signer.filter(signerInputValue => 'type' in signerInputValue);
    return reduce(
      signerInputValues,
      (acc, { property, value, type }) => {
        if ((type as any) === 'address') {
          return {
            ...acc,
            [`${property}∙${i}`]: {
              line1: get(value, 'line1'),
              line2: get(value, 'line2'),
              city: get(value, 'city'),
              stateCode: get(value, 'stateCode'),
              postalCode: get(value, 'postalCode'),
              countryCode: get(value, 'countryCode'),
            },
          };
        } else if ((type as any) === 'date') {
          return {
            ...acc,
            // changes dates to MM/DD/YYYY to match UI component requirements
            [`${property}∙${i}`]: formatYMD(value),
          };
        } else {
          return { ...acc, [`${property}∙${i}`]: value };
        }
      },
      {},
    );
  });
  return {
    ...(reduce(signersInitialValues, extend) as Record<string, any>),
    notes,
    notaryLocation: notaryLocation?.value,
  };
};

export const getEntryTaskInfo = (entry: NotaryAPI.JournalEntryResponse, documents: DocumentsAPI.DocumentsResponse) => {
  const { appointmentInformation, signers, recordings, notes } = entry;
  const signerNamesArray = signers.length > 0 ? getSignerNames(signers) : [];
  const signerNames = signerNamesArray.length > 0 ? signerNamesArray.join(', ') : 'N/A';
  const appointmentInformationCard = getAppointmentCardInfo(appointmentInformation, signerNames);
  const signerInputs = flatten(
    signers.map((signer, i) => {
      return {
        display: signerNamesArray[i],
        imageName: 'person',
        inputs: getSignerInputs(signer, i),
      };
    }),
  );
  const signerInformation = signers.map((signer, i) => getSignerCardInfo(signer, signerNamesArray[i]));

  const noteInput = getNoteInput();
  // Documents which allowDownload = false should not be displayed because they are documents from another calls
  const completedDocuments = documents.filter(
    document => document.status === DocumentStatus.COMPLETED && document.allowDownload,
  );
  const documentsInformation = getDocumentsCardInfo(completedDocuments);
  const initialValues = getInitialValues(signers, notes.value, entry.notaryLocation);
  const processedRecordings = recordings?.length
    ? recordings.map(item => ({
        id: uuidV4(),
        header: 'Appointment Recording',
        subHeader: item?.fileName || 'Processing...',
        downloadUrl: item?.downloadUrl || undefined,
      }))
    : emptyRecordings;
  return {
    id: entry.id,
    appointmentInformation: appointmentInformationCard,
    title: entry.title,
    signerInputs,
    signerInformation,
    recordings: processedRecordings,
    notesInput: noteInput,
    documents: documentsInformation,
    initialValues,
    notaryLocation: getNotaryLocationInput(entry.notaryLocation),
  };
};

type AttachmentWithId = IAttachment & { id: string };
const emptyRecordings: AttachmentWithId[] = [
  {
    id: 'empty-recording-id',
    header: 'Appointment Recording',
    subHeader: 'Processing...',
    downloadUrl: undefined,
  },
];

export const taskFormValuesToEntry = (formValues: Record<string, any>, entry: NotaryAPI.JournalEntryResponse) => {
  const newSignerValues = {} as Record<string, any>;
  for (const key in formValues) {
    if (formValues.hasOwnProperty(key)) {
      const signerIndex = key.substr(key.length - 1);
      if (!newSignerValues.hasOwnProperty(signerIndex)) {
        newSignerValues[signerIndex] = {};
      }
      newSignerValues[signerIndex][key.substr(0, key.length - 2)] = formValues[key];
    }
  }

  const { signers } = entry;
  const newSigners = signers.map((signer, i) => {
    return {
      firstName: find(signer, { property: 'firstName' })!.value,
      middleName: find(signer, { property: 'middleName' })?.value,
      lastName: find(signer, { property: 'lastName' })!.value,
      suffix: find(signer, { property: 'suffix' })?.value,
      address: newSignerValues[i].address,
      notes: newSignerValues[i].notes,
      idvProvider: find(signer, { property: 'idvProvider' })!.value,
      kbaProvider: find(signer, { property: 'kbaProvider' })!.value,
      credentialType: newSignerValues[i].credentialType,
      // converts date back to YYYY-MM-DD to update backend
      credentialIssuanceDate: formatMDY(newSignerValues[i].credentialIssuanceDate),
      credentialExpirationDate: formatMDY(newSignerValues[i].credentialExpirationDate),
      ...(find(signer, { property: 'credentialNumber' }) && {
        credentialNumber: newSignerValues[i].credentialNumber || null,
      }),
      additionalFields: {
        ...(find(signer, { property: 'isInCalifornia' }) && {
          isInCalifornia: find(signer, { property: 'isInCalifornia' })?.value,
        }),
        ...(find(signer, { property: 'signatureKey' }) && {
          signature: {
            file: {
              key: find(signer, { property: 'signatureKey' })?.value,
            },
          },
        }),
      },
    };
  });

  const fees = Number(formValues.fees as string) >= 0 ? Number(formValues.fees) * CENTS : DEFAULT_FEE_VALUE * CENTS;

  return {
    signers: newSigners,
    notes: formValues.notes,
    fees: {
      currency: 'USD',
      amountCents: fees,
    },
    notaryLocation: {
      line1: formValues.notaryLocation?.line1,
      line2: formValues.notaryLocation?.line2,
      city: formValues.notaryLocation?.city,
      postalCode: formValues.notaryLocation?.postalCode,
      stateCode: formValues.notaryLocation?.stateCode,
      county: formValues.notaryLocation?.county,
      countryCode: formValues.notaryLocation?.countryCode,
    },
  };
};

export const getDocumentsCardInfo = (documents: DocumentsAPI.DocumentsResponse) => {
  return documents.map(document => {
    return {
      primary: document.status,
      value: document.name,
    };
  });
};
